import { getProxiedImageUrl, getThumbnailUrl } from '../../../utils/imageProxy';
import { extractDraftJobId } from '../../../utils/draftFinal';
import {
  getExtension,
  IMAGE_EXTENSIONS,
  is3DContentType,
  is3DModelUrl,
  resolveIs3D,
} from '../../../utils/mediaTypeDetection';
import type { GalleryImage } from './GalleryContent';
import type { GenerationSlot } from '../../../contexts/WidgetContext';

/**
 * Infer content type from URL string (handles JSON array strings).
 */
export const inferContentTypeFromUrl = (urlData: string | null | undefined): string | undefined => {
  if (!urlData) return undefined;

  let url = urlData;
  try {
    const parsed = JSON.parse(urlData);
    if (Array.isArray(parsed) && parsed.length > 0) {
      url = parsed[0];
    }
  } catch {
    // Not JSON, use as-is
  }

  // Strip query string / hash before matching: signed asset URLs (fal.media,
  // presigned R2) carry `?token=...`, which an anchored extension regex misses —
  // the model/video would silently fall through to `image/jpeg`.
  url = url.split('?')[0].split('#')[0];

  if (url.match(/\.(mp4|webm|mov|avi|mkv|m4v)$/i)) {
    return 'video/mp4';
  }
  if (url.match(/\.(mp3|wav|ogg|flac|m4a|aac|mpeg)$/i)) {
    return 'audio/mpeg';
  }
  // Model extensions come from the canonical `MODEL_EXTENSIONS` set rather than
  // a local regex, so a new format is recognised everywhere at once.
  if (is3DModelUrl(url)) {
    return '3d';
  }
  return 'image/jpeg';
};

/** Parse the first URL from a JSON array string, with fallback chain. */
export function getFirstUrl(...sources: (string | undefined | null)[]): string | null {
  for (const source of sources) {
    if (!source) continue;
    try {
      const urls = JSON.parse(source);
      if (Array.isArray(urls) && urls.length > 0) return urls[0];
    } catch {
      // skip
    }
  }
  return null;
}

function parseUrlArray(source: string | null | undefined): string[] {
  if (!source) return [];
  try {
    const urls: unknown = JSON.parse(source);
    return Array.isArray(urls) ? urls.filter((url): url is string => typeof url === 'string') : [];
  } catch {
    return [];
  }
}

const MODEL_PROVIDER_ENDPOINT_FILENAMES = new Set(['download.php']);

/**
 * True when a 3D generation URL could be the model itself.
 *
 * Extensionless provider endpoints remain eligible because Dify and other
 * providers commonly serve GLBs from paths such as `file-preview`. A small
 * allowlist covers known script endpoints. Other dotted filenames are treated
 * as companion artifacts rather than being sent to the model viewer.
 */
function isPotentialModelUrl(url: string): boolean {
  if (url.trim().length === 0) return false;
  if (is3DModelUrl(url)) return true;

  const cleanUrl = url.split('?')[0].split('#')[0];
  const lastPathSegment = cleanUrl.split('/').pop()?.toLowerCase() ?? '';
  return (
    (lastPathSegment.length > 0 && !lastPathSegment.includes('.')) ||
    MODEL_PROVIDER_ENDPOINT_FILENAMES.has(lastPathSegment)
  );
}

/** Find the model asset, preferring sources with explicit model extensions. */
export function findModel3DUrl(
  contentType: string | null | undefined,
  originalUrls: string | null | undefined,
  externalUrls: string | null | undefined,
  previewUrls: string | null | undefined,
): string | null {
  return findModel3DUrls(contentType, originalUrls, externalUrls, previewUrls)[0] ?? null;
}

export function findModel3DUrls(
  contentType: string | null | undefined,
  originalUrls: string | null | undefined,
  externalUrls: string | null | undefined,
  previewUrls: string | null | undefined,
): string[] {
  const sources = [originalUrls, externalUrls, previewUrls].map(parseUrlArray);
  for (const urls of sources) {
    const explicitModelUrls = [...new Set(urls.filter(url => is3DModelUrl(url)))];
    if (explicitModelUrls.length > 0) {
      if (!is3DContentType(contentType)) return explicitModelUrls;

      // Each URL is processed independently during ingestion. A transient R2
      // failure can therefore leave an extensionless provider URL beside an
      // uploaded .glb in the same array; both are successful model outputs.
      // Preserve provider order so the first output remains the primary asset.
      return [...new Set(urls.filter(isPotentialModelUrl))];
    }
  }

  if (!is3DContentType(contentType)) return [];
  for (const urls of sources) {
    const potentialModelUrls = [...new Set(urls.filter(isPotentialModelUrl))];
    if (potentialModelUrls.length > 0) return potentialModelUrls;
  }
  return [];
}

/** Find the first image URL, preferring preview, then original, then external sources. */
export function findPosterImageUrl(
  previewUrls: string | null | undefined,
  originalUrls: string | null | undefined,
  externalUrls: string | null | undefined,
): string | null {
  for (const source of [previewUrls, originalUrls, externalUrls]) {
    for (const url of parseUrlArray(source)) {
      if (IMAGE_EXTENSIONS.includes(getExtension(url))) {
        return url;
      }
    }
  }

  return null;
}

/** Parse all URLs from a JSON array string, with fallback chain. */
function getAllUrls(...sources: (string | undefined | null)[]): string[] {
  for (const source of sources) {
    if (!source) continue;
    try {
      const urls = JSON.parse(source);
      if (Array.isArray(urls) && urls.length > 0) return urls;
    } catch {
      // skip
    }
  }
  return [];
}

/** Parse prompt text from raw_input JSON. */
export function getPromptFromRawInput(rawInput: string): string {
  try {
    const input = JSON.parse(rawInput);
    if (input.prompt) return input.prompt;
    if (input.text) return input.text;
    if (input.inputs?.prompt) return input.inputs.prompt;
    if (input.inputs?.text) return input.inputs.text;
    if (typeof input === 'string') return input;
    return 'No prompt';
  } catch {
    return 'Invalid prompt data';
  }
}

/**
 * Convert a GalleryImage to a GenerationSlot for use in MediaPreviewModal or TextResultModal.
 * Returns null if the image has failed status or has no usable URLs (except for text type).
 */
export function galleryImageToSlot(image: GalleryImage): GenerationSlot | null {
  if (image.status !== 'success') return null;

  const contentType = image.contentType || inferContentTypeFromUrl(
    image.preview_urls || image.original_urls || image.external_urls
  );
  const isText = contentType === 'text';
  const isVideo = contentType?.startsWith('video');
  const isAudio = contentType?.startsWith('audio');
  // URL fallback as in `GenerationSlot` / `MediaPreviewModal`: the gallery API
  // returns `content_type || 'image'`, so `contentType` is never empty on this
  // path and the inference above cannot fire. Without the URL check a model
  // whose content type is missing or stale in the DB would be pushed through
  // the CF Images / proxy pipeline, which a .glb cannot survive.
  // Scan every candidate: a provider may put a poster before the mesh in the
  // same array, while extensionless model URLs still need the 3D content type.
  const model3dUrls = findModel3DUrls(
    contentType,
    image.original_urls,
    image.external_urls,
    image.preview_urls,
  );
  const model3dUrl = model3dUrls[0] ?? null;
  const is3D = resolveIs3D(contentType, model3dUrl);

  // Text type: no URLs needed, just text content
  if (isText) {
    return {
      id: image.id.toString(),
      status: 'completed',
      prompt: getPromptFromRawInput(image.raw_input),
      settings: { imageSize: '', guidanceScale: 0, safetyTolerance: '', numInferenceSteps: 0 },
      contentType: 'text',
      textContent: image.text_content || extractTextFromRawResponse(image.raw_response_json),
      timestamp: new Date(image.created_at).getTime(),
      isFavorite: false,
      mark: image.mark ?? null,
      metadata: {
        userEmail: image.user_email,
        workflowName: image.workflow_name,
        createdAt: image.created_at,
        collections: image.collections,
      },
      rawInput: image.raw_input,
    };
  }

  // Original URL (first) - prioritize original > external > preview
  const originalUrl = is3D && model3dUrl
    ? model3dUrl
    : getFirstUrl(image.original_urls, image.external_urls, image.preview_urls);

  // All original URLs - same priority
  const allOriginals = is3D && model3dUrl
    ? model3dUrls
    : getAllUrls(image.original_urls, image.external_urls, image.preview_urls);

  // Preview URLs - prioritize preview > original > external, with thumbnailing for images
  const rawPreviewUrls = is3D && model3dUrl
    ? model3dUrls
    : getAllUrls(image.preview_urls, image.original_urls, image.external_urls);
  // 3D models must stay untouched like video/audio: the CF Images thumbnail
  // transform and the image proxy would both mangle a .glb the viewer needs.
  const allPreviews = rawPreviewUrls.map(url =>
    (isVideo || isAudio || is3D) ? url : getThumbnailUrl(url, 400)
  );
  const previewUrl = allPreviews.length > 0 ? allPreviews[0] : undefined;

  if (!originalUrl && !previewUrl) return null;

  return {
    id: image.id.toString(),
    status: 'completed',
    prompt: getPromptFromRawInput(image.raw_input),
    settings: { imageSize: '', guidanceScale: 0, safetyTolerance: '', numInferenceSteps: 0 },
    originalUrl: originalUrl ? ((isVideo || is3D) ? originalUrl : getProxiedImageUrl(originalUrl)) : undefined,
    previewUrl: previewUrl,
    originalUrls: allOriginals.map(url => (isVideo || is3D) ? url : getProxiedImageUrl(url)),
    previewUrls: allPreviews,
    contentType: contentType,
    timestamp: new Date(image.created_at).getTime(),
    isFavorite: false,
    mark: image.mark ?? null,
    metadata: {
      userEmail: image.user_email,
      workflowName: image.workflow_name,
      createdAt: image.created_at,
      collections: image.collections,
      originalUrlUnproxied: originalUrl ?? undefined,
      allOriginalUrlsUnproxied: allOriginals,
    },
    rawInput: image.raw_input,
    // Lets the preview offer "Finalize 1080p" on drafts (see draftFinal utils).
    workflowId: image.workflow_id,
    draftJobId: extractDraftJobId(image.raw_response_json) ?? undefined,
  };
}

/** Extract text content from raw_response_json as fallback when text_content column is empty. */
function extractTextFromRawResponse(rawResponseJson: string): string | undefined {
  if (!rawResponseJson) return undefined;
  try {
    const response = JSON.parse(rawResponseJson);
    if (response?.data?.outputs?.text) return response.data.outputs.text;
    if (response?.outputs?.text) return response.outputs.text;
    if (typeof response === 'string') return response;
    return undefined;
  } catch {
    return undefined;
  }
}

/** Keys that are system/internal and should not be displayed as generation parameters. */
const SYSTEM_KEYS = new Set([
  'prompt', 'prompt_v2', 'text', 'image_urls', 'video_urls', 'audio_urls',
  'inputs', 'num_images', 'gen_count', 'guidance_scale', 'safety_tolerance',
  'num_inference_steps', '_hidden_fields',
]);

/** Priority params shown first in compact card view. */
const PRIORITY_PARAMS = [
  'mode_name', 'model_type', 'model', 'quality',
  'duration', 'aspect_ratio', 'need_audio', 'seed',
];

export interface GenerationParam {
  key: string;
  label: string;
  value: string;
}

export interface ExtractParamsOptions {
  booleanTrue?: string;
  booleanFalse?: string;
}

/** Format a snake_case key to Title Case (e.g. "mode_name" → "Mode Name"). */
export function formatParamKey(key: string): string {
  return key
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Format a parameter value to a human-readable string. */
export function formatParamValue(value: unknown, options?: ExtractParamsOptions): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? (options?.booleanTrue ?? 'Yes') : (options?.booleanFalse ?? 'No');
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    const stringItems = value.filter(item => item !== null && item !== undefined && typeof item !== 'object');
    return stringItems.length > 0 ? stringItems.join(', ') : null;
  }
  if (typeof value === 'object') return null;
  return String(value);
}

/**
 * Extract displayable generation parameters from raw_input JSON string.
 * Filters out system keys and hidden fields, formats keys/values for display.
 * Also checks nested `inputs` object for backward compatibility with older records.
 * Returns sorted array, max 15 params. Priority params come first.
 */
export function extractGenerationParams(rawInput: string | undefined | null, options?: ExtractParamsOptions): GenerationParam[] {
  if (!rawInput) return [];

  let parsed: Record<string, unknown>;
  try {
    const result = JSON.parse(rawInput);
    if (typeof result !== 'object' || result === null || Array.isArray(result)) return [];
    parsed = result as Record<string, unknown>;
  } catch {
    return [];
  }

  // Build set of hidden field IDs to exclude (stored by backend for admin-configured hidden fields)
  const hiddenFields = Array.isArray(parsed._hidden_fields)
    ? new Set(parsed._hidden_fields.map(String))
    : new Set<string>();

  const params: GenerationParam[] = [];
  const seenKeys = new Set<string>();

  // Helper to process key-value entries
  const processEntry = (key: string, value: unknown) => {
    if (SYSTEM_KEYS.has(key)) return;
    if (hiddenFields.has(key)) return;
    if (seenKeys.has(key)) return;

    const formatted = formatParamValue(value, options);
    if (formatted === null || formatted === '') return;

    seenKeys.add(key);
    params.push({
      key,
      label: formatParamKey(key),
      value: formatted,
    });
  };

  // Process top-level keys
  for (const [key, value] of Object.entries(parsed)) {
    processEntry(key, value);
  }

  // Fallback: also check nested `inputs` object for backward compatibility
  const inputs = parsed.inputs;
  if (inputs && typeof inputs === 'object' && !Array.isArray(inputs)) {
    for (const [key, value] of Object.entries(inputs as Record<string, unknown>)) {
      processEntry(key, value);
    }
  }

  // Sort: priority params first (in PRIORITY_PARAMS order), then alphabetical
  params.sort((a, b) => {
    const aIdx = PRIORITY_PARAMS.indexOf(a.key);
    const bIdx = PRIORITY_PARAMS.indexOf(b.key);
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    return a.label.localeCompare(b.label);
  });

  return params.slice(0, 15);
}
